import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setCredentials, clearCredentials } from "./authSlice";

export interface CartItem {
  id: string;
  name: string;
  category: string;
  price: number;
  unit: string;
  madeBy: string;
  quantity: number;
  variantId?: number;
  maxStock?: number;
}

interface CartState {
  carts: { [userKey: string]: CartItem[] };
  currentUserKey: string;
}

const initialState: CartState = {
  carts: {},
  currentUserKey: "guest",
};

const getUserKey = (user: any) => {
  if (!user) {
    return "guest";
  }
  return user.phone_number ?? user.email ?? user.username ?? "guest";
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (
      state,
      action: PayloadAction<{ product: any; variant: any }>
    ) => {
      const { product, variant } = action.payload;
      const userKey = state.currentUserKey;
      if (!state.carts[userKey]) {
        state.carts[userKey] = [];
      }
      const variantUnit = variant.value ?? variant.weight ?? "";
      const variantStock = Number(
        variant.available_stock ?? variant.total_stock ?? variant.stock ?? 0
      );
      const unitPrice = Number(
        variant.display_price ?? variant.discounted_price ?? variant.price ?? 0
      );
      const cartItemId = `${product.id}-${variantUnit.replace(/\s+/g, "")}`;
      const existing = state.carts[userKey].find(
        item => item.id === cartItemId
      );
      if (existing) {
        const newQty = existing.quantity + 1;
        existing.quantity = variantStock
          ? Math.min(newQty, variantStock)
          : newQty;
      } else {
        state.carts[userKey].push({
          id: cartItemId,
          name: product.product_name ?? product.name ?? "",
          category:
            product.category_name ??
            product.category_details?.category_name ??
            product.category ??
            "Product",
          price: unitPrice,
          unit: variantUnit,
          madeBy:
            typeof product.is_handmade === "boolean"
              ? product.is_handmade
                ? "Our Elders"
                : "Jivandhara Team"
              : (product.madeBy ?? "Jivandhara Old Age Home"),
          quantity: 1,
          variantId: variant.id,
          maxStock: variantStock,
        });
      }
    },
    updateQuantity: (
      state,
      action: PayloadAction<{ cartItemId: string; delta: number }>
    ) => {
      const { cartItemId, delta } = action.payload;
      const userKey = state.currentUserKey;
      const cart = state.carts[userKey] || [];
      const existing = cart.find(item => item.id === cartItemId);
      if (existing) {
        const newQty = existing.quantity + delta;
        if (newQty <= 0) {
          state.carts[userKey] = cart.filter(item => item.id !== cartItemId);
        } else {
          const maxStock = existing.maxStock;
          existing.quantity = maxStock ? Math.min(newQty, maxStock) : newQty;
        }
      }
    },
    removeItem: (state, action: PayloadAction<string>) => {
      const cartItemId = action.payload;
      const userKey = state.currentUserKey;
      if (state.carts[userKey]) {
        state.carts[userKey] = state.carts[userKey].filter(
          item => item.id !== cartItemId
        );
      }
    },
    clearCart: state => {
      state.carts[state.currentUserKey] = [];
    },
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.carts[state.currentUserKey] = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(setCredentials, (state, action) => {
        const newKey = getUserKey(action.payload.user);
        const oldKey = state.currentUserKey;
        state.currentUserKey = newKey;

        // If logging in from guest to a registered user, merge guest items
        if (oldKey === "guest" && newKey !== "guest") {
          const guestCart = state.carts["guest"] || [];
          if (guestCart.length > 0) {
            const userCart = state.carts[newKey] || [];
            guestCart.forEach(gItem => {
              const existingIndex = userCart.findIndex(
                uItem => uItem.id === gItem.id
              );
              if (existingIndex >= 0) {
                const newQty =
                  userCart[existingIndex].quantity + gItem.quantity;
                const maxStock = userCart[existingIndex].maxStock;
                userCart[existingIndex].quantity = maxStock
                  ? Math.min(newQty, maxStock)
                  : newQty;
              } else {
                userCart.push(gItem);
              }
            });
            state.carts[newKey] = userCart;
            state.carts["guest"] = []; // Clear guest cart
          }
        }
      })
      .addCase(clearCredentials, state => {
        state.currentUserKey = "guest";
      });
  },
});

export const {
  addToCart,
  updateQuantity,
  removeItem,
  clearCart,
  setCartItems,
} = cartSlice.actions;

export default cartSlice.reducer;
